import { Route } from '@angular/router';
import { authGuardFn } from '@auth0/auth0-angular';
import { asmGuard } from './core/auth/asm.guard';
import { permissionGuard } from './core/auth/permission.guard';
import { Permissions } from './core/auto-generated/permissions';
import { dashboardGuard } from './pages/dashboard/guards/dashboard.guard';
import { SHIPMENTS_ROUTE_PATH } from './pages/shipments/shipments-route.constants';

export const routes: Route[] = [
  {
    path: '',
    canActivate: [authGuardFn],
    loadComponent: () =>
      import('./layout/main-layout').then((m) => m.MainLayout),
    children: [
      // Landing — empty StatusNow workspace
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () => import('./pages/dashboard/dashboard-page'),
      },

      {
        path: 'dashboards/:dashboardKey',
        title: 'Dashboard',
        canActivate: [dashboardGuard],
        loadComponent: () => import('./pages/dashboard/dashboard-page'),
      },

      //  home placeholder — internal-only reference page.
      {
        path: 'home',
        title: 'Home',
        canActivate: [permissionGuard],
        data: { permission: Permissions.DEV_ALL },
        loadComponent: () => import('./pages/home/home'),
      },

      // Forms demo — internal component-gallery reference, not a product page.
      {
        path: 'forms',
        title: 'Forms Demo',
        canActivate: [permissionGuard],
        data: { permission: Permissions.DEV_ALL },
        loadComponent: () => import('./pages/forms-demo/forms-demo'),
      },

      // My Profile — the signed-in user's self-service settings (authenticated;
      // no extra permission gate — every user manages their own profile).
      {
        path: 'my-profile',
        title: 'My Profile',
        loadComponent: () => import('./pages/my-profile/my-profile'),
      },

      // Shipments
      {
        path: SHIPMENTS_ROUTE_PATH,
        loadChildren: () =>
          import('./pages/shipments/shipments.routes').then((m) => m.default),
      },

      // Smart Inbox — the AI quoting inbox, gated by

      {
        path: 'smart-inbox',
        title: 'Smart Inbox',
        canActivate: [permissionGuard],
        data: { permission: Permissions.SMARTINBOX_READ },
        loadChildren: () =>
          import('./pages/smart-inbox/smart-inbox.routes').then(
            (m) => m.default
          ),
      },

      // Customer invoices (NetSuite) — gated by customers:viewinvoices
      {
        path: 'invoices',
        title: 'Invoices',
        canActivate: [permissionGuard],
        data: {
          permission: [Permissions.CUSTOMERS_VIEWINVOICES],
        },
        loadChildren: () =>
          import('./pages/invoices/invoices.routes').then((m) => m.default),
      },

      // Locations — section hub (shell + overview); the feature owns its routes.
      {
        path: 'locations',
        title: 'Locations',
        loadChildren: () =>
          import('./pages/locations/locations.routes').then((m) => m.default),
      },

      // Claims — list (authenticated) + detail (permission-gated inside its routes)
      {
        path: 'claims',
        title: 'Claims',
        loadChildren: () =>
          import('./pages/claims/claims.routes').then((m) => m.default),
      },

      // Compliance — supplier compliance hub (shell + tabs), gated by
      // compliance:manage. The feature owns its own context + child routes.
      {
        path: 'compliance',
        title: 'Compliance',
        canActivate: [permissionGuard],
        data: { permission: Permissions.COMPLIANCE_MANAGE },
        loadChildren: () =>
          import('./pages/compliance/compliance.routes').then((m) => m.default),
      },

      // Reports — back-office report library (gated by reports:view)
      {
        path: 'reports',
        title: 'Reports',
        loadChildren: () =>
          import('./pages/reports/reports.routes').then((m) => m.default),
      },

      // Admin — ASM-only back-office shell (persistent categorized sidebar
      // over a child outlet); the feature owns its per-screen permission guards.
      {
        path: 'admin',
        title: 'Admin',
        canActivate: [asmGuard],
        loadChildren: () =>
          import('./pages/admin/admin.routes').then((m) => m.default),
      },

      // Developers — internal tooling shell mirroring the Admin hub. The whole
      // subtree is gated by dev:all.
      {
        path: 'developers',
        title: 'Developers',
        canActivate: [permissionGuard],
        data: { permission: Permissions.DEV_ALL },
        loadChildren: () =>
          import('./pages/developers/developers.routes').then((m) => m.default),
      },

      // Carriers — section hub (shell + overview); the feature owns its routes.
      {
        path: 'carriers',
        title: 'Carriers',
        loadChildren: () =>
          import('./pages/carriers/carriers.routes').then((m) => m.default),
      },

      // Access Denied — blocked-user landing for signed-in accounts that lack

      // blocked-user page).
      {
        path: 'access-denied',
        title: 'Access Denied',
        loadComponent: () => import('./pages/access-denied/access-denied'),
      },

      // Catch all
      { path: '**', redirectTo: '' },
    ],
  },
];
